import { handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { FilterNamesEnum } from '@/client/helpers';
import {
  Actions,
  Subjects,
  SystemRoles,
  User,
  UsersBulk,
} from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import {
  CLOSED_USER_CREATION_WARNING_FORM,
  CLOSED_USER_CREATION_WARNING_IMPORT,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import {
  loginMethodOptions,
  notSystemRoleOptions,
  roleTranslations,
  systemRoleOptions,
  userOriginOptions,
} from '@/common/constants/roles';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterType,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { PermanentDeleteUserModal } from '@/components/users/PermanentDeleteUserModal';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  useAccountUsers,
  useDeleteUser,
  useDeleteUsersBulk,
  usePermanentlyRemoveUser,
  useResendWelcomeEmail,
  useRestoreUser,
  useUpdateUser,
} from '@/hooks/query';
import { usePlanners } from '@/hooks/query/planner.hooks';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useImpersonation } from '@/hooks/useImpersonation';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { useUsersImportFlow } from '@/hooks/useUsersImportFlow';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppButton, AppDropdownButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import AppTag from '@/ui/tag/AppTag';
import {
  branchAdminCheck,
  isDemoRestrictedMode,
  isMultiAdminRestriction,
  showBlockEmailsMessage,
} from '@/utils/helpers';
import { getBranchName } from '@/utils/helpers/branches.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import {
  FREE_TRIAL_USER_LIMIT,
  isTrialRestricted,
} from '@/utils/posthog/posthog.helper';
import { AxiosError } from 'axios';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TrialLimitBanner } from '../TrialLimitBanner';
import { UsersExportModal } from '../UsersExportModal';
import { UserBulkDeleteControls } from './UserBulkDeleteControls';

const PendingDeletionTag = styled(AppTag)`
  .p-tag-value {
    text-transform: none;
    font-weight: 400;
    font-size: 12px;
  }
`;

const StyledRestoreDialog = styled(Dialog)`
  &.p-dialog {
    border-radius: var(--medium-border-radius);

    .p-dialog-header {
      padding: var(--semi-big-padding) var(--semi-big-padding) 0;
    }

    .p-dialog-title {
      font-size: var(--modal-heading-font-size);
      font-weight: 700;
      line-height: 1.4;
    }

    .p-dialog-content {
      padding: var(--semi-xsmall-padding) var(--semi-big-padding) 0;
      font-size: var(--small-font-size);
      line-height: 1.5;
    }

    .p-dialog-footer {
      padding: 31px var(--semi-big-padding) var(--semi-big-padding);
      display: flex;
      gap: var(--semi-xsmall-padding);
    }
  }
`;

const StyledPendingDeletionEmptyContainer = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  overflow: hidden;

  > div:first-child {
    padding: var(--medium-padding);
  }
`;

const StyledPendingDeletionEmptyState = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 42px;
  text-align: center;
  background: var(--beige-main);
  border-radius: 0 0 var(--semi-medium-border-radius)
    var(--semi-medium-border-radius);

  .empty-state-icon {
    opacity: 0.4;
    font-size: 59px;
  }

  h3 {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    margin: 10px 0 0;
  }

  p {
    font-size: var(--small-font-size);
    line-height: 1.4;
    margin: 10px 0 0;
  }
`;

type UserDatatableProps = {
  defaultFilters?: FiltersType;
  defaultTableFilters?: Record<string, any>;
  withActions?: boolean;
  withToolbar?: boolean;
  withBranches?: boolean;
  withGroups?: number;
  withAddNew?: boolean;
  setBulkModalType?: (
    value: React.SetStateAction<UsersBulk | undefined>,
  ) => void;
  additionalColumns?: DataTableColumnType[];
  toolbarAdditional?: ReactNode;
  shouldRefetch?: boolean;
  withImport?: boolean;
  branchId?: string;
  groupId?: string;
  isPhishingGroup?: boolean;
  onUserCountChange?: (
    allUsersCount: number,
    filteredUsersCount: number,
  ) => void;
};

export const UserDatatable: React.FC<UserDatatableProps> = ({
  defaultFilters = [],
  defaultTableFilters = {},
  withActions = false,
  withToolbar = false,
  withBranches = false,
  withGroups = 0,
  withAddNew = false,
  setBulkModalType,
  additionalColumns,
  toolbarAdditional,
  shouldRefetch,
  withImport,
  branchId,
  groupId,
  isPhishingGroup,
  onUserCountChange,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = usePermission();
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const isDomainVerificationEnabled = canUseFeature(
    PostHogFeatureFlags.USER_DOMAIN_VERIFICATION,
  );
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUserId = currentUser?.id;
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);
  const { startImpersonation } = useImpersonation();

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage } = useTable({
    tableName: TableNamesEnum.USERS_LIST,
  });

  const { setDialogData } = useContext(DialogContext);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const pendingBulkDeleteIdsRef = useRef<Set<string>>(new Set());
  const [restoreUserId, setRestoreUserId] = useState<string | null>(null);
  const [permanentDeleteUser, setPermanentDeleteUser] = useState<User | null>(
    null,
  );

  const canBulkDelete =
    can(Actions.DELETE, Subjects.USERS) &&
    (!currentAccount?.isSystem ||
      currentUser?.role.code === SystemRoles.DEVELOPER) &&
    !isDemoAccount &&
    !isBranchAdmin;

  const addSortNulls = (sort: [string, 'asc' | 'desc'] | null) => {
    if (!sort) return undefined;
    const columnName = sort[0];
    const order = sort[1];

    switch (columnName) {
      case 'lastLogin':
        return order === 'asc' ? 'last' : 'first';
      default:
        return undefined;
    }
  };

  const { isLoading, users, refetch, isFetching } = useAccountUsers({
    accountId: currentAccount?.id,
    take,
    skip,
    filters: [...apiFilters, ...defaultFilters],
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
    withBranches,
    withGroups,
    sortNulls: addSortNulls(sort),
  });

  const { users: totalUsers, refetch: refetchTotalUsers } = useAccountUsers({
    accountId: currentAccount?.id,
  });

  const isAtTrialUserLimit =
    !!currentAccount &&
    isTrialRestricted(currentAccount) &&
    (canUseFeature(PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS) ||
      accountCanUseFeature(
        PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS,
        currentAccount?.featureFlagAbility,
      )) &&
    (totalUsers?.count ?? 0) >= FREE_TRIAL_USER_LIMIT;

  const selectedItemsForPage = useMemo(
    () =>
      users?.result?.filter((row) =>
        selectedUsers.some((s) => s.id === row.id),
      ) ?? [],
    [users?.result, selectedUsers],
  );

  const handleSelectionChange = useCallback(
    (newSelection: User[] | undefined) => {
      const newSelectionArr = newSelection ?? [];
      const currentPageIds = new Set(users?.result?.map((r) => r.id) ?? []);

      setSelectedUsers((prev) => {
        const keptFromOtherPages = prev.filter(
          (u) => !currentPageIds.has(u.id),
        );
        return [...keptFromOtherPages, ...newSelectionArr];
      });
    },
    [users?.result],
  );

  useEffect(() => {
    if (totalUsers?.count === undefined || users?.count === undefined) return;

    onUserCountChange?.(totalUsers?.count, users?.count);
  }, [totalUsers, users?.result]);
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const deleteUsersBulk = useDeleteUsersBulk();
  const resendWelcomeEmail = useResendWelcomeEmail();
  const restoreUser = useRestoreUser();
  const permanentlyRemoveUser = usePermanentlyRemoveUser();

  const canSeePendingDeletion =
    can(Actions.UPDATE, Subjects.USERS) &&
    !isBranchAdmin &&
    canUseFeature(Subjects.ACCOUNT_DATA_REMOVAL);

  const isPendingDeletionView = apiFilters.some(
    (f: any) => f.field === 'active' && f.value === 'pending-deletion',
  );

  const navigate = useNavigate();

  const onRowClick = (event: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(RedirectPaths[RedirectPathsEnum.USERS_EDIT](event.data.id), {
      state: {
        pathname: location.pathname,
        search: location.search,
      },
    });
  };

  const getFilters = (t: TranslationFunctionType) => [
    {
      label: t('generic.id'),
      field: 'id',
      type: FilterTypeEnum.TEXT,
    },
    {
      label: t('generic.name'),
      field: FilterNamesEnum.NAME,
      type: FilterTypeEnum.TEXT,
      placeholder: t('user.search.name'),
    },
    {
      label: t('generic.username'),
      field: 'username',
      type: FilterTypeEnum.TEXT,
      placeholder: t('user.search.username'),
    },
    {
      label: t('generic.email'),
      field: FilterNamesEnum.USER_BY_EMAIL,
      type: FilterTypeEnum.TEXT,
      placeholder: t('user.searchEmail'),
    },
    {
      label: t('branch'),
      type: FilterTypeEnum.SELECT,
      field: FilterNamesEnum.BRANCH,
      placeholder: t('generic.select'),
    },
    ...(can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin
      ? [
          {
            label: t('groups'),
            type: FilterTypeEnum.SELECT,
            field: FilterNamesEnum.GROUPS,
            placeholder: t('generic.select'),
          },
        ]
      : []),
    {
      label: t('filter.status'),
      field: 'active',
      type: FilterTypeEnum.SELECT,
      placeholder: t('filter.status'),
      options: [
        { label: t('generic.active'), value: 'active' },
        { label: t('generic.inactive'), value: 'inactive' },
        ...(canSeePendingDeletion
          ? [
              {
                label: t('user.pendingDeletion'),
                value: 'pending-deletion',
              },
            ]
          : []),
      ],
    },
    {
      label: t('filter.creationType'),
      field: FilterNamesEnum.USER_BY_ORIGIN,
      type: FilterTypeEnum.SELECT,
      placeholder: t('filter.creationType'),
      options: userOriginOptions(t),
    },
    ...(currentAccount?.meta?.isSSO
      ? [
          {
            label: t('filter.loginMethod'),
            field: FilterNamesEnum.USER_BY_LOGIN_METHOD,
            type: FilterTypeEnum.SELECT,
            placeholder: t('filter.loginMethod'),
            options: loginMethodOptions(t),
          },
        ]
      : []),
    {
      label: t('generic.role'),
      field: 'role',
      type: FilterTypeEnum.SELECT,
      placeholder: t('generic.role'),
      options: currentAccount?.isSystem
        ? systemRoleOptions(t)
        : notSystemRoleOptions(t),
    },
    {
      label: t('generic.companyName'),
      field: FilterNamesEnum.COMPANY_NAME,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.companyName'),
    },
    {
      label: t('generic.country'),
      field: FilterNamesEnum.COUNTRY,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.country'),
    },
    {
      label: t('generic.officeLocation'),
      field: FilterNamesEnum.OFFICE_LOCATION,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.officeLocation'),
    },
    {
      label: t('generic.department'),
      field: FilterNamesEnum.DEPARTMENT,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.department'),
    },
    {
      label: t('generic.manager'),
      field: FilterNamesEnum.MANAGER,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.manager'),
    },
    {
      label: t('generic.managerEmail'),
      field: FilterNamesEnum.MANAGER_EMAIL,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.managerEmail'),
    },
    {
      label: t('generic.jobTitle'),
      field: FilterNamesEnum.JOB,
      type: FilterTypeEnum.MULTI_SELECT,
      placeholder: t('filter.jobTitle'),
    },
    {
      label: t('generic.mobilePhone'),
      field: FilterNamesEnum.MOBILE_PHONE,
      type: FilterTypeEnum.TEXT,
      placeholder: t('filter.mobilePhone'),
    },
    {
      label: t('user.manually.deactivated'),
      field: FilterNamesEnum.MANUALLY_DEACTIVATED,
      type: FilterTypeEnum.SELECT_YES_NO,
      placeholder: t('generic.select'),
    },
    ...(isDomainVerificationEnabled
      ? [
          {
            label: t('user.domain.status'),
            field: FilterNamesEnum.DOMAIN_VERIFIED,
            type: FilterTypeEnum.SELECT,
            placeholder: t('generic.select'),
            options: [
              { label: t('user.domain.verified'), value: 'true' },
              { label: t('user.domain.notVerified'), value: 'false' },
            ],
          },
        ]
      : []),
  ];

  let columns: DataTableColumnType[] = [
    {
      field: 'id',
      header: t('generic.id'),
      sortable: true,
      filterable: false,
    },
    {
      field: 'firstName',
      header: t('generic.name'),
      sortable: true,
      filterable: false,
      frozen: true,
      render: (row: User) => row.name ?? <span>&#8212;</span>,
    },
    {
      field: 'username',
      header: t('generic.username'),
      sortable: true,
      filterable: false,
      render: (row: User) => row.username,
    },
    {
      field: 'email',
      header: t('generic.email'),
      sortable: true,
      filterable: false,
      render: (row: User) => row.email,
    },
    {
      field: 'branch',
      header: t('branch'),
      sortable: true,
      filterable: false,
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.branch ? (
            <span>{getBranchName(row.branch).reverse().join(' / ')}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    ...((can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin
      ? [
          {
            field: 'groups',
            header: t('groups'),
            sortable: true,
            filterable: false,
            render: (row: User) => (
              <FlexContainer direction="column" align="flex-start">
                {row?.groups?.length ? (
                  <span>{row.groups?.map(({ name }) => name)?.join(', ')}</span>
                ) : (
                  <div className="group-row">&#8212;</div>
                )}
              </FlexContainer>
            ),
          },
        ]
      : []) as DataTableColumnType[]),
    {
      field: 'created',
      header: t('generic.creation'),
      sortable: true,
      filterable: false,
      render: (row: User) =>
        row?.created ? <FormatDate date={row?.created} /> : t('generic.never'),
    },
    {
      field: 'updated',
      header: t('generic.updated'),
      sortable: true,
      filterable: false,
      render: (row: User) =>
        row?.updated ? <FormatDate date={row?.updated} /> : t('generic.never'),
    },
    {
      field: 'active',
      header: t('filter.status'),
      sortable: !isPendingDeletionView,
      filterable: false,
      render: (row: User) => {
        if (row.deletedAt) {
          const daysRemaining = getDaysUntilRemoval(
            row.deletedAt,
            row.gracePeriodDays ?? 30,
          );
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <PendingDeletionTag
                value={t('user.pendingDeletion')}
                type="warning"
              />
              <span
                style={{
                  fontSize: 'var(--xxsmall-font-size)',
                  color: 'var(--gray-darker)',
                  fontWeight: 500,
                }}
              >
                {formatRemainingTime(daysRemaining)}
              </span>
            </div>
          );
        }
        return row.active ? t('generic.active') : t('generic.inactive');
      },
    },
    {
      field: 'origin',
      header: t('generic.creationType'),
      sortable: true,
      filterable: false,
      render: (row: User) => t(`user.type.${row.origin}`),
    },
    ...((currentAccount?.meta?.isSSO
      ? [
          {
            field: 'loginMethod',
            header: t('user.loginMethod'),
            sortable: false,
            filterable: false,
            render: (row: User) =>
              row.origin === UserOrigin.MANUAL ||
              row.origin === UserOrigin.DEFAULT
                ? t('user.loginMethod.manual')
                : t('user.loginMethod.sso'),
          },
        ]
      : []) as DataTableColumnType[]),
    {
      field: 'role',
      header: t('generic.role'),
      sortable: true,
      filterable: false,
      render: (row: User) =>
        row.accessState === 'pending'
          ? t('pendingAccess.role.pending')
          : roleTranslations(row.role, t),
    },
    {
      field: 'companyName',
      header: t('generic.companyName'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.companyName ? (
            <span>{row?.companyData?.companyName}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'country',
      header: t('generic.country'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.country ? (
            <span>{row?.companyData?.country}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'officeLocation',
      header: t('generic.officeLocation'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.officeLocation ? (
            <span>{row?.companyData?.officeLocation}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'department',
      header: t('generic.department'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.department ? (
            <span>{row?.companyData?.department}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'manager',
      header: t('generic.manager'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.manager ? (
            <span>{row?.companyData?.manager}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'managerEmail',
      header: t('generic.managerEmail'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.managerEmail ? (
            <span>{row?.companyData?.managerEmail}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'jobTitle',
      header: t('generic.jobTitle'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.jobTitle ? (
            <span>{row?.companyData?.jobTitle}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'mobilePhone',
      header: t('generic.mobilePhone'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: User) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.companyData?.mobilePhone ? (
            <span>{row?.companyData?.mobilePhone}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'lastLogin',
      header: t('user.lastLogin'),
      sortable: true,
      filterable: false,
      render: (row: User) =>
        row?.meta?.lastLogin ? (
          <FormatDate
            date={row?.meta?.lastLogin}
            format={DateFormats.TIMEDATE}
          />
        ) : (
          <span>&#8212;</span>
        ),
    },
    ...((isDomainVerificationEnabled
      ? [
          {
            field: 'domainVerified',
            header: t('user.domain.status'),
            sortable: true,
            filterable: false,
            render: (row: User) =>
              row.domainVerified ? (
                <AppChip
                  label={t('user.domain.verified')}
                  type="primary"
                  size="s"
                />
              ) : (
                <AppChip
                  label={t('user.domain.notVerified')}
                  type="red"
                  size="s"
                />
              ),
          },
        ]
      : []) as DataTableColumnType[]),
  ];

  if (withActions) {
    columns = [
      ...columns,
      {
        field: 'actions',
        header: t('generic.actions'),
        sortable: false,
        filterable: false,
        style: {
          width: '80px',
          textAlign: 'center',
        },
        render: (row: User) => {
          if (deletingUserId === row.id) {
            return (
              <FlexContainer>
                <ProgressSpinner style={{ width: '24px', height: '24px' }} />
              </FlexContainer>
            );
          }

          if (isPendingDeletionView && row.deletedAt) {
            const pendingMenuItems: MenuItem[] = [
              {
                label: t('user.restore'),
                icon: 'pi pi-replay',
                command: () => setRestoreUserId(row.id),
                disabled: isAtTrialUserLimit,
                template: isAtTrialUserLimit
                  ? (item: any) => (
                      <>
                        <div
                          id={`restore-disabled-${row.id}`}
                          className="p-menuitem-content"
                          style={{ pointerEvents: 'auto' }}
                        >
                          <a
                            className="p-menuitem-link p-disabled"
                            style={{ opacity: 0.5 }}
                          >
                            <span className="p-menuitem-icon pi pi-replay" />
                            <span className="p-menuitem-text">
                              {item.label}
                            </span>
                          </a>
                        </div>
                        <Tooltip
                          target={`#restore-disabled-${row.id}`}
                          content={t('user.freeTrial.limitReached.deletion')}
                          position="left"
                          pt={{
                            root: {
                              dir: document.documentElement.dir || undefined,
                            },
                          }}
                        />
                      </>
                    )
                  : undefined,
              },
              { separator: true },
              {
                label: t('user.deletePermanently'),
                icon: 'pi pi-trash',
                className: 'p-menuitem-danger',
                command: () => setPermanentDeleteUser(row),
              },
            ];

            return <DataTableActions menuItems={pendingMenuItems} />;
          }

          return (
            <DataTableActions
              disabled={menuItems(row).length < 1 || !!deletingUserId}
              menuItems={menuItems(row)}
            />
          );
        },
      },
    ];
  }

  if (additionalColumns && additionalColumns.length > 0) {
    columns = [...columns, ...additionalColumns];
  }

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const defaultVisibleColumns = columns
    .filter(
      (column) =>
        ![
          'id',
          'created',
          'companyName',
          'country',
          'officeLocation',
          'department',
          'manager',
          'jobTitle',
          'mobilePhone',
          'loginMethod',
          'domainVerified',
        ].includes(column.field),
    )
    .map((column) => column.field);
  const alwaysVisibleColumns = ['firstName', 'actions'];
  //

  useEffect(() => {
    if (!shouldRefetch) {
      return;
    }

    refetch();
    refetchTotalUsers();
  }, [shouldRefetch]);

  const getDaysUntilRemoval = (
    deletedAt: string,
    gracePeriodDays: number,
  ): number => {
    const deletedDate = new Date(deletedAt);
    const removalDate = new Date(deletedDate);
    removalDate.setDate(removalDate.getDate() + gracePeriodDays);
    const now = new Date();
    const diffMs = removalDate.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const formatRemainingTime = (days: number): string => {
    if (days <= 0) return t('user.permanentlyRemovedToday');
    if (days === 1) return t('user.permanentlyRemovedTomorrow');
    return t('user.permanentlyRemovedInDays', { days });
  };

  const handleRestoreUser = async (userId: string) => {
    try {
      await restoreUser.restore({
        userId,
        accountId: (currentAccount as Account)?.id,
      });
      await refetch();
      await refetchTotalUsers();
      toast?.success(t('toast.success'), t('user.restored'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const isPermanentDeleteRef = useRef(false);

  const handlePermanentlyRemoveUser = async (userId: string) => {
    setPermanentDeleteUser(null);
    isPermanentDeleteRef.current = true;
    setUserId(userId);

    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnected.current) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnected.current) {
        permanentlyRemoveUser.permanentlyRemove({
          userId,
          accountId: (currentAccount as Account)?.id,
        });
        setDeletingUserId(userId);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setDeletingUserId(null);
      isPermanentDeleteRef.current = false;
    }
  };

  const menuItems = (user: User) => {
    const menu: MenuItem[] = [];
    const cannotManageMultiAdminsBool = isMultiAdminRestriction(
      user,
      currentUser,
    );

    if (
      can(Actions.UPDATE, Subjects.USERS) &&
      (!currentAccount?.isSystem ||
        [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
          currentUser?.role.code as SystemRoles,
        ))
    ) {
      menu.push(
        ...[
          {
            items: [
              {
                label: t('generic.edit'),
                icon: 'pi pi-pencil',
                command: () => {
                  navigate(
                    RedirectPaths[RedirectPathsEnum.USERS_EDIT](user.id),
                  );
                },
              },
              {
                label: user.active
                  ? t('generic.deactivate')
                  : t('generic.activate'),
                icon: user.active ? 'pi pi-minus-circle' : 'pi pi-check',
                command: isDemoAccount
                  ? undefined
                  : () => {
                      return cannotManageMultiAdminsBool
                        ? setDialogData({
                            type: DialogType.INFO,
                            show: true,
                            header:
                              currentAccount?.platformSettings?.name ?? '',
                            message: t('generic.contactSupport', {
                              platformName:
                                currentAccount?.platformSettings?.name,
                            }),
                          })
                        : setDialogData({
                            type: DialogType.CONFIRMATION,
                            show: true,
                            header: `${
                              user.active
                                ? t('generic.deactivate')
                                : t('generic.activate')
                            } ${t('dialog.confirm')}`,
                            message: user.active
                              ? t('user.deactivate')
                              : t('user.activate'),
                            onAccept: async () =>
                              handleToggleItemsStatus(user.id, user.active),
                          });
                    },
              },
            ],
          },
        ],
      );
    }

    if (
      currentUser &&
      [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
        currentUser.role.code,
      ) &&
      user.id !== currentUser.id && // Can't impersonate yourself
      user.active // Can only impersonate active users
    ) {
      menu.push({
        label: t('user.impersonate'),
        icon: 'pi pi-user',
        command: () => handleImpersonateUser(user),
        disabled: !user.meta?.keycloakId && !user?.meta?.externalUser?.id,
      });
    }

    if (
      can(Actions.UPDATE, Subjects.USERS) &&
      user.active &&
      (!currentAccount?.blockSystemEmails ||
        user.role?.code !== SystemRoles.LEARNER) &&
      !isDemoAccount
    ) {
      menu.push({
        label: t('user.resendWelcomeEmail'),
        icon: 'pi pi-envelope',
        command: () => handleResendWelcomeEmail(user),
      });
    }

    if (
      can(Actions.DELETE, Subjects.USERS) &&
      (!currentAccount?.isSystem ||
        [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
          currentUser?.role.code as SystemRoles,
        ))
    ) {
      menu.push(
        ...[
          {
            label: t('generic.delete'),
            icon: 'pi pi-times',
            command: isDemoAccount
              ? undefined
              : () =>
                  cannotManageMultiAdminsBool
                    ? setDialogData({
                        type: DialogType.INFO,
                        show: true,
                        header: currentAccount?.platformSettings?.name ?? '',
                        message: t('generic.contactSupport', {
                          platformName: currentAccount?.platformSettings?.name,
                        }),
                      })
                    : setDialogData({
                        type: DialogType.CONFIRMATION,
                        show: true,
                        header: t('dialog.delete'),
                        message: canSeePendingDeletion
                          ? t('dialog.delete.user.withGracePeriod', {
                              days: currentAccount?.gracePeriodDays ?? 30,
                            })
                          : t('dialog.delete.user'),
                        onAccept: () => handleDeleteUser(user.id),
                      }),
          },
        ],
      );
    }

    return menu;
  };

  const handleToggleItemsStatus = async (userId: string, active: boolean) => {
    if (isDemoAccount) return;
    try {
      await updateUser.update({
        userId: userId,
        accountId: (currentAccount as Account)?.id,
        updates: { active: !active },
      });
      await refetch();
      toast?.success(
        t('toast.success'),
        active ? t('user.deactivated') : t('user.activated'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleResendWelcomeEmail = (user: User) => {
    setDialogData({
      type: DialogType.CONFIRMATION,
      show: true,
      header: t('user.resendWelcomeEmail'),
      message: t('user.resendWelcomeEmail.confirm'),
      onAccept: async () => {
        try {
          await resendWelcomeEmail.resend({
            userId: user.id,
            accountId: (currentAccount as Account)?.id,
          });
          toast?.success(
            t('toast.success'),
            t('user.resendWelcomeEmail.success'),
          );
        } catch {
          toast?.error(t('toast.error'), t('user.resendWelcomeEmail.error'));
        }
      },
    });
  };

  const handleImpersonateUser = async (user: User) => {
    if (!currentAccount?.id) {
      return;
    }

    if (
      user.meta?.externalUser &&
      !user.meta?.firstLogin &&
      !user.meta?.lastLogin
    ) {
      toast?.error(t('toast.error'), t('impersonation.neverLoggedIn'));
      return;
    }

    try {
      await startImpersonation(user.id);
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const {
    lastMessage,
    messagesQueueRef,
    setNotificationParam: setUserId,
    notificationParam: notificationUserId,
    isConnected,
  } = useNotifications(client.users.userNotifyUrl, { queueAll: true });

  const lastProcessedIndexRef = useRef(0);

  useEffect(() => {
    if (!notificationUserId) return;

    if (bulkDeleting) {
      const queue = messagesQueueRef.current;
      const start = lastProcessedIndexRef.current;

      for (let i = start; i < queue.length; i += 1) {
        handleSyncNotification(queue[i]);
      }
      lastProcessedIndexRef.current = queue.length;
    } else {
      handleSyncNotification(lastMessage);
    }
  }, [notificationUserId, lastMessage]);

  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const handleDeleteUser = async (userId: string) => {
    setUserId(userId);

    // Wait for a notify connection to establish
    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnected.current) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnected.current) {
        deleteUser.delete({
          userId,
          accountId: (currentAccount as Account)?.id,
        });
        setDeletingUserId(userId);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setDeletingUserId(null);
    }
  };

  const deletedUsersRef = useRef<Set<string>>(new Set());
  const successfullyDeletedUsersRef = useRef<Set<string>>(new Set());
  const handleSyncNotification = async (messages: { data: any } | null) => {
    if (
      !messages?.data?.event ||
      !['user.delete.finished', 'user.delete.error'].includes(
        messages?.data?.event,
      )
    )
      return;

    const userIdFromEvent = messages.data.payload?.id;

    if (bulkDeleting && userIdFromEvent) {
      if (messages.data.event === 'user.delete.error') {
        toast?.error(t('toast.error'), t('users.bulk.not.deleted'));
      }

      deletedUsersRef.current.add(userIdFromEvent);

      if (messages.data.event === 'user.delete.finished') {
        successfullyDeletedUsersRef.current.add(userIdFromEvent);
      }

      if (
        deletedUsersRef.current.size === pendingBulkDeleteIdsRef.current.size
      ) {
        setBulkDeleting(false);
        setShowBulkDeleteModal(false);
        setUserId(undefined);
        setSelectedUsers([]);

        await refetch();
        await refetchTotalUsers();

        toast?.success(
          t('toast.success'),
          t('user.deleted.count', {
            count: successfullyDeletedUsersRef.current.size,
          }),
        );

        messagesQueueRef.current = [];
        lastProcessedIndexRef.current = 0;
        deletedUsersRef.current = new Set();
        successfullyDeletedUsersRef.current = new Set();
      }

      return;
    }

    if (messages.data.event === 'user.delete.error') {
      toast?.error(t('toast.error'), t('user.not.deleted'));
      isPermanentDeleteRef.current = false;
      setDeletingUserId(null);
      setUserId(undefined);
      await refetch();
      await refetchTotalUsers();
    }

    if (messages.data.event === 'user.delete.finished') {
      const toastMessage = isPermanentDeleteRef.current
        ? t('user.permanentlyDeleted')
        : t('user.deleted');
      toast?.success(t('toast.success'), toastMessage);
      isPermanentDeleteRef.current = false;
      setDeletingUserId(null);
      setUserId(undefined);
      await refetch();
      await refetchTotalUsers();
    }
  };

  const handleBulkDeleteConfirm = async (usersToDelete: User[]) => {
    const userIds = usersToDelete
      .map((u) => u.id)
      .filter((id) => id !== currentUserId);
    const accountId = (currentAccount as Account)?.id;

    pendingBulkDeleteIdsRef.current = new Set(userIds);
    if (!userIds.length) {
      setShowBulkDeleteModal(false);
      return;
    }

    setBulkDeleting(true);
    try {
      setUserId(accountId);

      // Wait for a notify connection to establish
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected.current) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      if (isConnected.current) {
        await deleteUsersBulk.deleteBulk({
          userIds,
          accountId,
        });
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setUserId(undefined);
      pendingBulkDeleteIdsRef.current.clear();
      setBulkDeleting(false);
      setShowBulkDeleteModal(false);
    }
  };

  const refetchAll = useCallback(() => {
    refetch();
    refetchTotalUsers();
  }, [refetch, refetchTotalUsers]);

  const {
    isUploading,
    renderFileUpload,
    importModal: usersImportModal,
  } = useUsersImportFlow({
    branchId,
    groupId,
    refetch: refetchAll,
  });

  const itemActionMenu = useRef<any>(null);

  const toggleItemActionMenu = (event: any) => {
    if (!itemActionMenu.current) {
      return;
    }

    itemActionMenu.current.toggle(event);
  };

  const { planners } = usePlanners(
    {
      skip: 0,
      take: 0,
    },
    currentAccount?.id,
  );

  const exportImportMenuItems: MenuItem[] = [
    {
      label: t('users.import.csvXlsx'),
      template: () => {
        const isShown =
          !localStorage.getItem(CLOSED_USER_CREATION_WARNING_IMPORT) &&
          planners?.count;

        return (
          <>
            {isShown ? (
              <div className="p-menuitem-content">
                <div
                  className="p-menuitem-link"
                  onClick={() => {
                    if (isShown) {
                      setDialogData({
                        show: true,
                        type: DialogType.WARNING,
                        header: t('dialog.enrollment.warning.heading'),
                        message: t('dialog.enrollment.warning'),
                        confirmationMessage: t('dialog.enrollment.doNotShow'),
                        confirmationConst: CLOSED_USER_CREATION_WARNING_IMPORT,
                        showDialogConfirmation: true,
                        showUpload: (
                          resetDialogData: () => void,
                          onDoNotShowAgain: () => void,
                        ) =>
                          renderFileUpload({
                            className: 'button',
                            chooseLabel: t('generic.continue'),
                            resetDialogData,
                            onDoNotShowAgain,
                          }),
                      });
                    }
                  }}
                >
                  <span className="p-menuitem-icon pi pi-plus" />
                  <span className="p-menuitem-text">
                    {t('users.import.csvXlsx')}
                  </span>
                </div>
              </div>
            ) : (
              renderFileUpload({
                className: 'file-upload',
                chooseLabel: t('users.import.csvXlsx'),
                chooseIcon: (
                  <span
                    className="p-menuitem-icon pi pi-plus"
                    style={{ marginRight: '8px' }}
                  />
                ),
              })
            )}
          </>
        );
      },
    },
    {
      label: t('users.export.userList'),
      icon: 'pi pi-download',
      command: () => setShowExportModal(true),
    },
    {
      label: t('users.download.template'),
      icon: 'pi pi-file',
      command: async () => {
        try {
          await client.users.downloadImportTemplate(
            currentAccount?.id as string,
          );
        } catch (e) {
          handleAxiosError(e, toast);
        }
      },
    },
  ];

  const addRemoveMenuItems: MenuItem[] = [
    {
      label: t('users.add'),
      icon: 'pi pi-plus',
      command: () =>
        setBulkModalType ? setBulkModalType(UsersBulk.ADD) : null,
    },
    {
      label: t('users.remove'),
      icon: 'pi pi-minus',
      command: () =>
        setBulkModalType ? setBulkModalType(UsersBulk.REMOVE) : null,
    },
  ];

  const hasBlockEmailsMessage = showBlockEmailsMessage(currentAccount);

  const handleCreateNewRedirect = () => {
    const isShown =
      !localStorage.getItem(CLOSED_USER_CREATION_WARNING_FORM) &&
      planners?.count &&
      !hasBlockEmailsMessage;
    if (isShown) {
      setDialogData({
        show: true,
        type: DialogType.WARNING,
        header: t('dialog.enrollment.warning.heading'),
        message: t('dialog.enrollment.warning'),
        confirmationMessage: t('dialog.enrollment.doNotShow'),
        confirmationConst: CLOSED_USER_CREATION_WARNING_FORM,
        showDialogConfirmation: true,
        onAccept: () =>
          navigate(RedirectPaths[RedirectPathsEnum.USERS_CREATE]()),
      });
    } else {
      navigate(RedirectPaths[RedirectPathsEnum.USERS_CREATE]());
    }
  };

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFilters(t) as FilterType[]}
          onFilter={onFilter}
          defaultValues={defaultTableFilters}
          tableName={TableNamesEnum.USERS_LIST}
        />
        <FlexContainer width="auto" gap={8} className="users-actions">
          {withAddNew &&
            can(Actions.CREATE, Subjects.USERS) &&
            currentAccount?.active &&
            (!currentAccount?.isSystem ||
              [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
                currentUser?.role.code as SystemRoles,
              )) &&
            (isAtTrialUserLimit ? (
              <TrialLimitBanner
                description={t(
                  isPendingDeletionView
                    ? 'user.freeTrial.limitReached.deletion'
                    : 'user.freeTrial.limitReached.description',
                )}
              >
                <AppButton
                  label={t('button.createNew')}
                  severity="secondary"
                  isDisabled
                />
              </TrialLimitBanner>
            ) : (
              <AppButton
                label={t('button.createNew')}
                severity="secondary"
                onClick={isDemoAccount ? undefined : handleCreateNewRedirect}
              />
            ))}
          {!!setBulkModalType &&
            (!currentAccount?.isSystem ||
              [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
                currentUser?.role.code as SystemRoles,
              )) &&
            !isPhishingGroup && (
              <AppDropdownButton
                menuId="add-remove-users"
                severity="secondary"
                label={t('users.addOrRemove')}
                className="w-fit-content "
                items={isDemoAccount ? [] : addRemoveMenuItems}
                isDisabled={isBranchAdmin}
                tooltip={
                  isBranchAdmin
                    ? t('generic.feature.notAvailable.branchAdmins')
                    : undefined
                }
                tooltipOptions={{ position: 'top', showOnDisabled: true }}
              />
            )}
          {can(Actions.CREATE, Subjects.USERS) &&
            !currentAccount?.isSystem &&
            currentAccount?.active &&
            withImport && (
              <>
                <Menu
                  model={exportImportMenuItems}
                  popup
                  ref={itemActionMenu}
                  id="overlay_menu"
                  style={{ minWidth: '250px' }}
                />
                <AppButton
                  severity={isPhishingGroup ? 'success' : 'info'}
                  label={
                    isPhishingGroup
                      ? t('generic.export')
                      : t('generic.import.export')
                  }
                  aria-controls="overlay_menu"
                  onClick={
                    isDemoAccount
                      ? undefined
                      : isPhishingGroup
                      ? () => setShowExportModal(true)
                      : toggleItemActionMenu
                  }
                  state={
                    isUploading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                  }
                  icon={isPhishingGroup ? 'pi pi-download' : undefined}
                />
              </>
            )}
          {toolbarAdditional}
        </FlexContainer>
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.USERS_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  return (
    <>
      <UserBulkDeleteControls
        canBulkDelete={canBulkDelete}
        selectedUsers={selectedUsers}
        bulkDeleting={bulkDeleting}
        showModal={showBulkDeleteModal}
        setShowModal={setShowBulkDeleteModal}
        onConfirm={handleBulkDeleteConfirm}
        onDeselectAll={() => setSelectedUsers([])}
      />
      {isPendingDeletionView &&
      !isLoading &&
      (!users?.result || users.result.length === 0) ? (
        <StyledPendingDeletionEmptyContainer>
          {withToolbar && toolbar}
          <StyledPendingDeletionEmptyState>
            <i className="pi pi-check-circle empty-state-icon" />
            <h3>{t('user.pendingDeletion.empty')}</h3>
            <p>
              {t('user.pendingDeletion.emptyDescription.line1')}
              <br />
              {t('user.pendingDeletion.emptyDescription.line2')}
            </p>
          </StyledPendingDeletionEmptyState>
        </StyledPendingDeletionEmptyContainer>
      ) : (
        <DataTable
          data={users?.result}
          count={users?.count}
          isLoading={isLoading}
          isFetching={isFetching}
          toolbar={withToolbar ? toolbar : null}
          columns={columns}
          visibleColumns={visibleColumns}
          rows={take}
          skip={skip}
          onPage={onPage}
          onSort={onSort}
          sort={sort}
          onRowClick={
            can(Actions.UPDATE, Subjects.USERS) && !isPendingDeletionView
              ? onRowClick
              : undefined
          }
          selectedItems={
            canBulkDelete && !isPendingDeletionView
              ? selectedItemsForPage
              : undefined
          }
          setSelectedItems={
            canBulkDelete && !isPendingDeletionView
              ? handleSelectionChange
              : undefined
          }
          stickyScrollbar
        />
      )}
      <UsersExportModal
        defaultFilters={branchId || groupId ? [] : defaultFilters}
        filters={apiFilters}
        visible={showExportModal}
        onHide={() => setShowExportModal(false)}
        branchId={branchId}
        groupId={groupId}
        visibleColumns={visibleColumns}
      />
      {usersImportModal}
      <PermanentDeleteUserModal
        user={permanentDeleteUser}
        onConfirm={handlePermanentlyRemoveUser}
        onClose={() => setPermanentDeleteUser(null)}
      />
      <StyledRestoreDialog
        visible={!!restoreUserId}
        onHide={() => setRestoreUserId(null)}
        header={t('dialog.restore.user.header')}
        closable={false}
        draggable={false}
        footer={
          <>
            <AppButton
              label={t('button.restoreUser')}
              onClick={async () => {
                if (restoreUserId) {
                  await handleRestoreUser(restoreUserId);
                  setRestoreUserId(null);
                }
              }}
            />
            <AppButton
              label={t('button.cancel')}
              severity="secondary"
              type="outlined"
              onClick={() => setRestoreUserId(null)}
            />
          </>
        }
      >
        <p>{t('dialog.restore.user.message')}</p>
      </StyledRestoreDialog>
    </>
  );
};
